import {
  CollabConnectionClient,
  type CollabStateBridge,
  SerializerType,
  type CollabConnectionClientOptions,
  type DomainErrorEventData,
} from '@atlassian/collab-connection-client';

import { debugWarn } from '../network/debug-logger';
import { WebSocketCodes } from '../network/websocket-codes';

import {
  type Transport,
  type TransportCallbacks,
  type TransportContext,
} from './transport';

/**
 * A nominal type representing the peer ID of the sender of a collab message.
 * This is sourced from `metadata.from` in the collab-connection-client update payload.
 */
export type CollabConnectionClientSender = string & {
  readonly __collabConnectionClientSender: unique symbol;
};

// TODO: import this when exposed from collab-connection-client
export enum DisconnectReason {
  CLIENT_DISCONNECT = 'CLIENT_DISCONNECT',
  SERVER_DISCONNECT = 'SERVER_DISCONNECT',
  SOCKET_CLOSED = 'SOCKET_CLOSED',
  SOCKET_ERROR = 'SOCKET_ERROR',
  SOCKET_TIMEOUT = 'SOCKET_TIMEOUT',
  UNKNOWN_DISCONNECT = 'UNKNOWN_DISCONNECT',
}

export function disconnectReasonToWebSocketCode(
  reason: DisconnectReason
): WebSocketCodes {
  switch (reason) {
    case DisconnectReason.CLIENT_DISCONNECT:
      return WebSocketCodes.NORMAL_CLOSURE;
    case DisconnectReason.SERVER_DISCONNECT:
      return WebSocketCodes.EXCEPTION_CLOSURE;
    case DisconnectReason.SOCKET_CLOSED:
      return WebSocketCodes.NORMAL_CLOSURE;
    case DisconnectReason.SOCKET_ERROR:
      return WebSocketCodes.CLIENT_ERROR;
    case DisconnectReason.SOCKET_TIMEOUT:
      return WebSocketCodes.CLIENT_ERROR;
    case DisconnectReason.UNKNOWN_DISCONNECT:
      return WebSocketCodes.CLIENT_ERROR;
  }
}

// CNS-28626: Constants for detecting identity-failure upstream errors in domainError payloads.
// NCS returns this error code + status when a step is rejected due to an empty identity claim.
const IDENTITY_FAILURE_ERROR_CODE = 'UPSTREAM_SERVICE_UNAVAILABLE';
const IDENTITY_FAILURE_STATUS = 500;
const IDENTITY_FAILURE_CLOSE_REASON = 'identity-expired-reconnect';

export class CollabConnectionTransport implements Transport {
  private client: CollabConnectionClient<Uint8Array> | undefined;
  private disposed = false;

  constructor(
    private readonly ctx: TransportContext,
    private readonly callbacks: TransportCallbacks
  ) {}

  connect(): void {
    let analyticsWebClient;
    if (!this.ctx.getAnalyticsWebClient) {
      console.error('Not passing analytics client to C3');
    } else {
      analyticsWebClient = this.ctx.getAnalyticsWebClient;
    }

    if (this.ctx.stateBridge === undefined) {
      throw new Error('C3 client initialised without a state bridge');
    }

    const clientOptions: CollabConnectionClientOptions<Uint8Array> = {
      bridge: this.ctx.stateBridge as CollabStateBridge<Uint8Array<ArrayBufferLike>>,
      url: this.ctx.relayDetails.url,
      namespace: this.ctx.relayDetails.namespace,
      permissionTokenRefresh: () => this.ctx.accessTokenSupplier(),
      product: {
        product: 'ncs-canvas',
      },
      serializer: {
        type: SerializerType.JSON,
      },
      getAnalyticsWebClient: analyticsWebClient,
      throwOnNotConnected: false,
      ...(this.ctx.relayDetails.extraHeaders
        ? { extraHeaders: this.ctx.relayDetails.extraHeaders }
        : {}),
    };

    const client = new CollabConnectionClient(clientOptions);
    this.client = client;

    client.on('connected', () => {
      if (!this.disposed) {
        this.callbacks.onOpen();
      }
    });

    client.on('disconnected', (reason: DisconnectReason) => {
      if (!this.disposed) {
        const code = disconnectReasonToWebSocketCode(reason);
        this.callbacks.onClose(code, reason);
      }
    });

    client.on('error', (error: Error) => {
      if (!this.disposed) {
        this.callbacks.onError(error);
      }
    });

    // CNS-28626: When a sendWithAck fails because NCS rejected the step due to an
    // empty identity claim (UPSTREAM_SERVICE_UNAVAILABLE from submitToCanvasServer),
    // treat it as a transport close rather than a silent error. This surfaces the
    // identity failure to the Canvas state machine so it tears down this transport,
    // re-runs connecting:authenticating to fetch a fresh token, and reconnects —
    // giving the pending step a chance to be resent with valid identity.
    client.on('domainError', (payload: { data: DomainErrorEventData<Uint8Array> }) => {
      if (!this.disposed) {
        // NCS puts an UpstreamError shape ({ message, data: { code, status } }) into
        // domainError.error at runtime, but the C3 type declares it as DomainError
        // (flat shape: { message, code, meta? }). Cast through unknown and check for
        // the presence of 'data' to distinguish an UpstreamError from a DomainError
        // (which has no 'data' property) without importing isDomainError — the latter
        // is mocked away in tests and unavailable at the module level.
        const rawError: unknown = payload?.data?.error;
        const upstreamData =
          rawError !== null && typeof rawError === 'object' && 'data' in rawError
            ? (rawError as { data?: { code?: string; status?: number } }).data
            : undefined;
        const isIdentityFailure =
          upstreamData?.code === IDENTITY_FAILURE_ERROR_CODE &&
          upstreamData?.status === IDENTITY_FAILURE_STATUS;
        if (isIdentityFailure) {
          this.callbacks.onClose(
            WebSocketCodes.CLIENT_ERROR,
            IDENTITY_FAILURE_CLOSE_REASON
          );
        } else {
          debugWarn(
            '[CollabConnectionTransport:domainError] ⚠️ C3 domainError — isIdentityFailure=%s, upstreamCode=%s, upstreamStatus=%s, rawError=%s',
            isIdentityFailure,
            upstreamData?.code ?? 'none',
            upstreamData?.status ?? 'none',
            JSON.stringify(rawError)
          );
        }
      }
    });

    client.on(
      'update',
      (payload: { data: ArrayBuffer; metadata?: { from?: string } }) => {
        if (!this.disposed) {
          this.callbacks.onMessage(
            new Uint8Array(payload.data),
            payload.metadata?.from as CollabConnectionClientSender | undefined
          );
        }
      }
    );

    client.on(
      'transient',
      (payload: { data: ArrayBuffer; metadata?: { from?: string } }) => {
        if (!this.disposed) {
          this.callbacks.onTransientMessage?.(new Uint8Array(payload.data));
        }
      }
    );

    client.setOnAckTimeout(() => {
      if (!this.disposed) {
        this.callbacks.onAckTimeout?.();
      }
    });

    client.initialize();
  }

  // the data here isn't actually used, its just here to preserve the interface.
  // if c3 is set up properly with the statebridge, then sendThenConfirm here should automatically
  // retrieve the batch from the StateBridge object
  send(_data: Uint8Array<ArrayBufferLike>): void {
    this.client?.sendThenConfirm();
    return;
  }

  sendToPeer(data: Uint8Array<ArrayBufferLike>, peer: string): void {
    this.client?.sendToPeer({ name: 'transient', payload: data }, peer);
  }

  sendAwareness(data: Uint8Array<ArrayBufferLike>): void {
    this.client?.send({ name: 'transient', payload: data });
  }

  flush(ignoreAck?: boolean) {
    this.client?.flush(ignoreAck);
  }

  close(): void {
    this.client?.destroy();
  }

  dispose(): void {
    this.disposed = true;
    this.client?.destroy();
    this.client = undefined;
  }
}

export function createCollabConnectionTransport(
  ctx: TransportContext,
  callbacks: TransportCallbacks
): Transport {
  const transport = new CollabConnectionTransport(ctx, callbacks);
  transport.connect();
  return transport;
}
